import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle, Award, Users, Brain, TrendingUp, Lightbulb, BookOpen, Shield, ArrowLeft, Briefcase, Target, Cpu, BarChart3, Globe } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { SEO } from '../components/SEO';
import { useNavigate } from 'react-router-dom';
import { RelatedCoursesSection } from '../components/training/RelatedCoursesSection';

export const AITraining = () => {
  const navigate = useNavigate();

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Course",
    "name": "AI Training Program",
    "description": "An AI Training Program designed to teach the principles, technologies, and applications of artificial intelligence — including machine learning, deep learning, NLP, and data science — for career advancement and business innovation.",
    "provider": {
      "@type": "Organization",
      "name": "ISDE Academy",
      "sameAs": "https://isde.academy"
    },
    "educationalLevel": "Beginner to Advanced",
    "occupationalCredentialAwarded": {
      "@type": "EducationalOccupationalCredential",
      "credentialCategory": "Professional Certification",
      "name": "AI Training Certification"
    },
    "audience": {
      "@type": "Audience",
      "audienceType": "Students, IT Professionals, Data Scientists, Engineers, Business Analysts, Healthcare Professionals, Entrepreneurs"
    },
    "hasCourseInstance": {
      "@type": "CourseInstance",
      "courseMode": "Blended",
      "duration": "P3D"
    },
    "teaches": [
      "Fundamentals of Artificial Intelligence",
      "Machine Learning and Deep Learning",
      "Neural Networks (CNNs, RNNs)",
      "Natural Language Processing (NLP)",
      "Data Analysis and Model Development",
      "AI Tools, Platforms, and Programming",
      "Real-world AI Applications and Case Studies"
    ],
    "geographicArea": {
      "@type": "Country",
      "name": "India"
    }
  };

  const faqStructuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What is an AI Training Program?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "An AI Training Program is a structured learning course designed to teach the principles, technologies, and applications of artificial intelligence. It covers machine learning, deep learning, data science, AI tools and programming, and real-world applications. Programs combine theory with hands-on practice to ensure practical competence."
        }
      },
      {
        "@type": "Question",
        "name": "Who should enroll in an AI Training Program?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "AI training programs are suitable for students and fresh graduates, IT and software professionals, data analysts and data scientists, engineers and technical professionals, business analysts and managers, healthcare and finance professionals, and entrepreneurs and startup founders. Programs are designed with beginner-to-advanced learning paths."
        }
      },
      {
        "@type": "Question",
        "name": "What are the key concepts covered in AI training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Key concepts include AI fundamentals (types of AI, ethical AI), machine learning (supervised/unsupervised learning, model training), deep learning and neural networks (CNNs, RNNs, NLP basics), data science and analytics, and AI tools and programming using Python, AI libraries, and cloud platforms."
        }
      },
      {
        "@type": "Question",
        "name": "What career opportunities are available after an AI Training Program?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "After completing an AI Training Program, learners can pursue roles such as AI Engineer, Machine Learning Engineer, Data Scientist, AI Analyst, Business Intelligence Analyst, AI Consultant, and Automation Specialist across technology, healthcare, finance, retail, manufacturing, and government sectors."
        }
      },
      {
        "@type": "Question",
        "name": "How do I choose the right AI Training Program?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "When selecting an AI training program, consider curriculum aligned with industry needs, hands-on projects and practical exposure, experienced trainers and mentors, use of real-world datasets, recognized certification, and career support or guidance. A quality program balances theory with application."
        }
      },
      {
        "@type": "Question",
        "name": "What is the future scope of AI training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "The future of AI training continues to expand with generative AI and large language models, responsible and ethical AI, AI in healthcare and life sciences, AI-driven automation and robotics, and AI integration with IoT and cloud computing. Continuous learning in AI ensures long-term career growth."
        }
      }
    ]
  };

  const importancePoints = [
    {
      number: "1",
      title: "Rapid Adoption of AI Across Industries",
      description: "Organizations worldwide are integrating AI to improve efficiency, reduce costs, and enhance customer experience. Skilled AI professionals are critical to successful implementation.",
      icon: Globe
    },
    {
      number: "2",
      title: "High Demand and Skill Shortage",
      description: "There is a significant global shortage of AI-skilled professionals. AI training helps bridge this gap and opens doors to high-paying career opportunities.",
      icon: TrendingUp
    },
    {
      number: "3",
      title: "Business Innovation and Competitive Advantage",
      description: "AI enables predictive insights, automation, personalization, and innovation. Trained professionals help organizations stay competitive and future-ready.",
      icon: Lightbulb
    },
    {
      number: "4",
      title: "Automation and Digital Transformation",
      description: "AI training prepares individuals to work alongside intelligent systems, manage automation, and drive digital transformation initiatives.",
      icon: Cpu
    },
    {
      number: "5",
      title: "Career Resilience in a Changing Job Market",
      description: "As automation reshapes traditional roles, AI skills provide career stability and adaptability.",
      icon: Shield
    }
  ];

  const keyConcepts = [
    {
      number: "1",
      title: "Artificial Intelligence Fundamentals",
      intro: "Participants learn the core foundations of AI that are essential for understanding advanced concepts.",
      topics: [
        "What AI is and how it works",
        "Types of AI (narrow AI, general AI)",
        "AI vs machine learning vs deep learning",
        "Ethical and responsible AI"
      ],
      icon: Brain,
      color: "blue"
    },
    {
      number: "2",
      title: "Machine Learning",
      intro: "Machine learning is a core component of any AI training program. Learners gain hands-on experience building predictive models.",
      topics: [
        "Supervised and unsupervised learning",
        "Regression and classification",
        "Model training and evaluation",
        "Feature engineering"
      ],
      icon: BarChart3,
      color: "green"
    },
    {
      number: "3",
      title: "Deep Learning and Neural Networks",
      intro: "Advanced AI programs cover deep learning techniques that power modern AI applications such as image recognition and language translation.",
      topics: [
        "Artificial neural networks",
        "Convolutional Neural Networks (CNNs)",
        "Recurrent Neural Networks (RNNs)",
        "Natural Language Processing (NLP) basics"
      ],
      icon: Cpu,
      color: "purple"
    },
    {
      number: "4",
      title: "Data Science and Analytics",
      intro: "AI relies heavily on data. Strong data skills are essential for effective AI model development.",
      topics: [
        "Data collection and cleaning",
        "Exploratory data analysis",
        "Statistical concepts",
        "Data visualization"
      ],
      icon: BarChart3,
      color: "orange"
    },
    {
      number: "5",
      title: "AI Tools, Platforms, and Programming",
      intro: "Participants work with industry-relevant tools. Practical exposure ensures job-ready skills.",
      topics: [
        "Python for AI and machine learning",
        "AI libraries and frameworks",
        "Cloud-based AI platforms",
        "Model deployment basics"
      ],
      icon: Lightbulb,
      color: "teal"
    }
  ];

  const colorMap = {
    blue: { bg: "bg-blue-50", border: "border-blue-200", badge: "bg-blue-600 text-white", check: "text-blue-500", icon: "text-blue-600" },
    green: { bg: "bg-green-50", border: "border-green-200", badge: "bg-green-600 text-white", check: "text-green-500", icon: "text-green-600" },
    purple: { bg: "bg-purple-50", border: "border-purple-200", badge: "bg-purple-600 text-white", check: "text-purple-500", icon: "text-purple-600" },
    orange: { bg: "bg-orange-50", border: "border-orange-200", badge: "bg-orange-500 text-white", check: "text-orange-500", icon: "text-orange-600" },
    teal: { bg: "bg-teal-50", border: "border-teal-200", badge: "bg-teal-600 text-white", check: "text-teal-500", icon: "text-teal-600" }
  };

  const keyComponents = [
    {
      number: "1",
      title: "Conceptual Learning",
      description: "Participants develop a strong theoretical understanding of AI principles, algorithms, and use cases.",
      icon: BookOpen
    },
    {
      number: "2",
      title: "Hands-On Projects",
      description: "Most AI training programs emphasize practical learning through real-world datasets, mini projects, capstone projects, and case studies. This helps learners apply concepts to real business problems.",
      icon: Target,
      bullets: ["Real-world datasets", "Mini projects", "Capstone projects", "Case studies"]
    },
    {
      number: "3",
      title: "Industry Use Cases",
      description: "Training often includes AI applications across industries. Industry exposure enhances job relevance.",
      icon: Globe,
      bullets: ["Healthcare diagnostics", "Financial fraud detection", "Marketing personalization", "Supply chain optimization", "Predictive maintenance"]
    },
    {
      number: "4",
      title: "Assessment and Certification",
      description: "Learners are evaluated through assignments and quizzes, project submissions, and final assessments. Successful participants receive AI training certification, validating their skills.",
      icon: Award
    }
  ];

  const benefits = [
    {
      title: "Career Advancement",
      description: "AI professionals are among the most in-demand and well-paid talent globally.",
      icon: TrendingUp,
      color: "blue"
    },
    {
      title: "Cross-Industry Applicability",
      description: "AI skills are transferable across industries, increasing career flexibility.",
      icon: Globe,
      color: "green"
    },
    {
      title: "Improved Problem-Solving Skills",
      description: "AI training enhances analytical thinking, data-driven decision-making, and innovation.",
      icon: Brain,
      color: "purple"
    },
    {
      title: "Business Impact",
      description: "Professionals trained in AI help organizations improve efficiency, accuracy, and scalability.",
      icon: BarChart3,
      color: "orange"
    },
    {
      title: "Future-Proof Skill Set",
      description: "AI skills remain relevant as technology continues to evolve.",
      icon: Shield,
      color: "teal"
    }
  ];

  const benefitColors = {
    blue: { bg: "bg-blue-50", text: "text-blue-600" },
    green: { bg: "bg-green-50", text: "text-green-600" },
    purple: { bg: "bg-purple-50", text: "text-purple-600" },
    orange: { bg: "bg-orange-50", text: "text-orange-600" },
    teal: { bg: "bg-teal-50", text: "text-teal-600" }
  };

  const targetAudience = [
    "Students and fresh graduates",
    "IT and software professionals",
    "Data analysts and data scientists",
    "Engineers and technical professionals",
    "Business analysts and managers",
    "Healthcare and finance professionals",
    "Entrepreneurs and startup founders"
  ];

  const choosingCriteria = [
    "Curriculum aligned with industry needs",
    "Hands-on projects and practical exposure",
    "Experienced trainers and mentors",
    "Use of real-world datasets",
    "Recognized certification",
    "Career support or guidance"
  ];

  const careerOpportunities = [
    { title: "AI Engineer", icon: Cpu },
    { title: "Machine Learning Engineer", icon: Brain },
    { title: "Data Scientist", icon: BarChart3 },
    { title: "AI Analyst", icon: Target },
    { title: "Business Intelligence Analyst", icon: TrendingUp },
    { title: "AI Consultant", icon: Briefcase },
    { title: "Automation Specialist", icon: Lightbulb }
  ];

  const futureScope = [
    "Generative AI and large language models",
    "Responsible and ethical AI",
    "AI in healthcare and life sciences",
    "AI-driven automation and robotics",
    "AI integration with IoT and cloud computing"
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO
        title="AI Training Program | Artificial Intelligence Certification Course | ISDE Academy"
        description="Advance your career with an AI Training Program. Learn machine learning, AI models, deep learning, NLP, automation, and industry use cases. Earn AI certification."
        keywords="AI training program, artificial intelligence training, AI certification india, machine learning training, deep learning course, AI training program india, artificial intelligence course, AI training Chennai"
        canonical="https://isde.academy/ai-training-program"
        ogUrl="https://isde.academy/ai-training-program"
        structuredData={[structuredData, faqStructuredData]}
      />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-violet-900 via-purple-800 to-blue-900 text-white py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Button
              variant="ghost"
              className="text-purple-200 hover:text-white mb-6 p-0"
              onClick={() => navigate('/industry-training/information-technology')}
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to IT Training
            </Button>

            <div className="flex flex-wrap items-center gap-3 mb-4">
              <span className="bg-purple-500 bg-opacity-40 text-purple-100 text-sm font-medium px-3 py-1 rounded-full">
                Artificial Intelligence
              </span>
              <span className="bg-blue-500 bg-opacity-40 text-blue-100 text-sm font-medium px-3 py-1 rounded-full">
                Beginner to Advanced
              </span>
              <span className="bg-green-500 bg-opacity-40 text-green-100 text-sm font-medium px-3 py-1 rounded-full">
                Certification Included
              </span>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              AI Training Program
            </h1>

            <p className="text-lg md:text-xl text-purple-100 mb-4 max-w-4xl">
              An AI Training Program is a structured learning course designed to teach the principles, technologies, and applications of artificial intelligence. These programs focus on enabling learners to analyze data, build intelligent models, automate processes, and make data-driven decisions using AI techniques.
            </p>

            <p className="text-base md:text-lg text-purple-200 mb-4 max-w-4xl">
              AI training typically includes:
            </p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-w-3xl mb-6">
              {[
                "Fundamentals of artificial intelligence",
                "Machine learning and deep learning",
                "Data analysis and model development",
                "AI tools, platforms, and programming",
                "Real-world applications and case studies"
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-2 text-purple-100 text-sm">
                  <CheckCircle className="w-4 h-4 text-purple-300 flex-shrink-0" />
                  {item}
                </div>
              ))}
            </div>
            <p className="text-purple-200 max-w-3xl mb-8">
              AI training programs combine theory with hands-on practice to ensure practical competence.
            </p>

            <div className="flex flex-wrap gap-4">
              <Button
                size="lg"
                className="bg-white text-purple-900 hover:bg-purple-50 font-semibold"
                onClick={() => navigate('/contact')}
              >
                Enrol Now
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-purple-900 font-semibold"
                onClick={() => navigate('/training-calendar')}
              >
                View Schedule
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Importance Section */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Importance of an AI Training Program</h2>
            <div className="space-y-5">
              {importancePoints.map((point, idx) => {
                const Icon = point.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.08 }}
                    className="flex items-start gap-4 bg-gray-50 rounded-xl p-5 border border-gray-200"
                  >
                    <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-gray-900 mb-1">{point.number}. {point.title}</h3>
                      <p className="text-gray-600 text-sm">{point.description}</p>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Concepts */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="mb-10"
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Key Concepts Covered in an AI Training Program</h2>
          </motion.div>

          <div className="space-y-5">
            {keyConcepts.map((concept, idx) => {
              const colors = colorMap[concept.color];
              const Icon = concept.icon;
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 15 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08 }}
                  className={`rounded-xl border ${colors.border} ${colors.bg} p-6`}
                >
                  <div className="flex items-start gap-4 mb-4">
                    <span className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${colors.badge}`}>
                      {concept.number}
                    </span>
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <Icon className={`w-5 h-5 ${colors.icon}`} />
                        <h3 className="text-xl font-bold text-gray-900">{concept.title}</h3>
                      </div>
                      <p className="text-gray-600 text-sm mb-3">{concept.intro}</p>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {concept.topics.map((topic, tIdx) => (
                          <div key={tIdx} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className={`w-4 h-4 ${colors.check} flex-shrink-0 mt-0.5`} />
                            {topic}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Who Should Enroll */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Who Should Enroll in an AI Training Program?</h3>
            <p className="text-gray-600 mb-6 text-lg">
              An AI Training Program is suitable for a wide range of learners. Programs are often designed with beginner-to-advanced learning paths.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {targetAudience.map((audience, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, scale: 0.95 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.06 }}
                  className="bg-purple-50 border border-purple-200 rounded-lg p-4 flex items-start gap-3"
                >
                  <CheckCircle className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-sm font-medium">{audience}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Key Components */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Key Components of an AI Training Program</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {keyComponents.map((component, idx) => {
                const Icon = component.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, y: 15 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.08 }}
                  >
                    <Card className="h-full border-0 shadow-md">
                      <CardContent className="p-6">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                            <Icon className="w-5 h-5 text-purple-600" />
                          </div>
                          <h4 className="font-bold text-gray-900">{component.number}. {component.title}</h4>
                        </div>
                        <p className="text-gray-600 text-sm mb-3">{component.description}</p>
                        {component.bullets && (
                          <ul className="space-y-1">
                            {component.bullets.map((bullet, bIdx) => (
                              <li key={bIdx} className="flex items-start gap-2 text-sm text-gray-600">
                                <CheckCircle className="w-4 h-4 text-purple-500 flex-shrink-0 mt-0.5" />
                                {bullet}
                              </li>
                            ))}
                          </ul>
                        )}
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Benefits of an AI Training Program</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {benefits.map((benefit, idx) => {
                const Icon = benefit.icon;
                const colors = benefitColors[benefit.color];
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.08 }}
                  >
                    <Card className="h-full hover:shadow-lg transition-all hover:-translate-y-1">
                      <CardContent className="p-6">
                        <div className={`${colors.bg} w-12 h-12 rounded-lg flex items-center justify-center mb-4`}>
                          <Icon className={`w-6 h-6 ${colors.text}`} />
                        </div>
                        <h4 className="font-bold text-gray-900 mb-2">{benefit.title}</h4>
                        <p className="text-gray-600 text-sm">{benefit.description}</p>
                      </CardContent>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Choosing the Right Program */}
      <section className="py-14 bg-purple-900 text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h4 className="text-2xl md:text-3xl font-bold mb-3">Choosing the Right AI Training Program</h4>
            <p className="text-purple-200 mb-8 text-lg">
              When selecting an AI training program, consider these factors. A quality AI training program balances theory with application.
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {choosingCriteria.map((criterion, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 10 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.07 }}
                  className="bg-purple-800 bg-opacity-60 border border-purple-700 rounded-lg p-4 flex items-start gap-3"
                >
                  <CheckCircle className="w-5 h-5 text-purple-300 flex-shrink-0 mt-0.5" />
                  <span className="text-purple-100 text-sm font-medium">{criterion}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Career Opportunities */}
      <section className="py-14 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h4 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Career Opportunities After an AI Training Program</h4>
            <p className="text-gray-600 mb-8 text-lg">
              AI professionals are in demand across technology, healthcare, finance, retail, manufacturing, and government sectors.
            </p>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {careerOpportunities.map((career, idx) => {
                const Icon = career.icon;
                return (
                  <motion.div
                    key={idx}
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.07 }}
                    className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-xl p-4 text-center"
                  >
                    <Icon className="w-8 h-8 text-purple-600 mx-auto mb-2" />
                    <p className="text-xs font-semibold text-gray-700 leading-tight">{career.title}</p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Future Scope */}
      <section className="py-14 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h4 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3">Future Scope of AI Training</h4>
            <p className="text-gray-600 mb-6 text-lg">
              The future of AI training continues to expand with emerging trends such as:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {futureScope.map((item, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -10 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: idx * 0.08 }}
                  className="bg-gradient-to-br from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4 flex items-start gap-3"
                >
                  <Brain className="w-5 h-5 text-purple-600 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-700 text-sm font-medium">{item}</span>
                </motion.div>
              ))}
            </div>
            <p className="text-gray-600 mt-6 text-lg font-medium">
              Continuous learning in AI ensures long-term career growth.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-14 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8">Frequently Asked Questions About AI Training Programs</h2>
            <div className="space-y-4">
              {faqStructuredData.mainEntity.map((faq, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-5">
                  <h3 className="font-semibold text-gray-900 mb-2">{faq.name}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{faq.acceptedAnswer.text}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Related Courses */}
      <RelatedCoursesSection currentSlug="iso-27001" slugs={['iso-27001', 'iso-9001', 'iso-22000']} heading="Related ISO Training Courses" />

      {/* CTA */}
      <section className="py-14 bg-gradient-to-r from-violet-900 to-purple-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Start Your AI Journey?</h2>
            <p className="text-purple-200 mb-8 text-lg">
              Join ISDE Academy's AI Training Program and build the skills needed to thrive in an AI-driven world. Available online, classroom, and hybrid modes across India including Chennai, Bangalore, Mumbai, and Hyderabad.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="bg-white text-purple-900 hover:bg-purple-50 font-semibold"
                onClick={() => navigate('/contact')}
              >
                Enrol in AI Training
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="border-white text-white hover:bg-white hover:text-purple-900 font-semibold"
                onClick={() => navigate('/training-calendar')}
              >
                View Training Calendar
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};
